import { Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  Observable,
  of,
  share,
  switchMap,
  takeWhile,
  tap,
  throwError,
  timer
} from 'rxjs';
import { ulid } from 'ulid';
import {
  DriversIntegrationTypes,
  FileData,
  FileUploadStatus,
  FilesUploadBody,
  FilesUploadResponse
} from 'src/app/projects/document-scan/drivers-integrations/drivers-integrations.types';
import { createFilesScanProject } from 'src/app/projects/document-scan/document-scan.component';
import { AuthService } from '../auth.service';
import { PosthogService } from '../posthog.service';
import { ScanProjectService } from '../scan-project.service';
import { ToastV2Service } from '../toast-v2.service';
import { UploadFileService } from '../upload-file.service';
import { BackgroundTaskRuntimeService } from './background-task-runtime.service';
import { NotificationCenterService } from './notification-center.service';
import { TaskRef } from './notification-center.types';
import {
  buildProgressFromEntries,
  buildResponseSummary,
  buildTaskStatusFromEntries,
  buildUploadEntries,
  buildUploadMessage
} from './upload-task.helpers';

interface UploadManagerEvents {
  onProcessStarted?: () => void;
  onProjectCreated?: () => void;
  onUploadError?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentsUploadNotificationsService {
  constructor(
    private readonly uploadService: UploadFileService,
    private readonly scanProjectService: ScanProjectService,
    private readonly toastService: ToastV2Service,
    private readonly posthogService: PosthogService,
    private readonly authService: AuthService,
    private readonly notificationCenter: NotificationCenterService,
    private readonly taskRuntime: BackgroundTaskRuntimeService
  ) {}

  startFilesScanProject(
    files: File[] | FilesUploadBody,
    keywordIds: string[],
    projectName: string,
    vendor: DriversIntegrationTypes,
    events: UploadManagerEvents = {}
  ): void {
    const projectId = ulid();
    let taskRef!: TaskRef;
    taskRef = this.notificationCenter.startTask({
      trackingKey: `documents-upload:${projectId}`,
      kind: 'upload',
      title: projectName ? `Uploading documents for ${projectName}` : 'Uploading documents',
      message: 'Preparing document upload.',
      status: 'queued',
      cancellable: true,
      route: `/dashboard/document-scan/documents/${projectId}`,
      entries: this.buildInitialEntries(files),
      actionLabel: 'Open project',
      actionDisabled: true,
      actionTooltip: 'Project is still being created. You can open it once creation is finished.',
      onCancel: async () => {
        await this.taskRuntime.cancelTask(taskRef.id);
      }
    });

    this.taskRuntime.registerTask(taskRef.id);
    this.taskRuntime.setCancelHandler(taskRef.id, async () => {
      this.cancelTask(taskRef);
    });

    this.startPolling(projectId, taskRef, events);

    const uploadObservable$: Observable<FilesUploadResponse> =
      'folderIds' in files
        ? this.uploadService.uploadExternalProviderFiles(vendor, files, projectId)
        : this.uploadService.uploadLocalPickedFiles(files, projectId);

    const uploadSubscription = uploadObservable$
      .pipe(
        catchError((error) => this.handleUploadError(error, taskRef, events)),
        switchMap((response) =>
          this.processUploadResponseAndCreateProject(response, keywordIds, projectName, projectId, taskRef, events)
        )
      )
      .subscribe({
        complete: () => {
          this.taskRuntime.cleanupTask(taskRef.id);
        },
        error: () => {
          this.taskRuntime.cleanupTask(taskRef.id);
        }
      });

    this.taskRuntime.attachSubscription(taskRef.id, uploadSubscription);
  }

  private handleUploadError(error: unknown, taskRef: TaskRef, events: UploadManagerEvents): Observable<never> {
    events.onUploadError?.();

    const currentTask = this.notificationCenter.items().find((item) => item.id === taskRef.id);
    const entries = currentTask?.type === 'task'
      ? (currentTask.entries ?? []).map((entry) =>
          entry.status === 'queued' || entry.status === 'running'
            ? {
                ...entry,
                status: 'error' as const,
                errorMessage: 'An unexpected error occurred during upload.'
              }
            : entry
        )
      : [];

    taskRef.fail('Error uploading files, please try again later!', {
      entries,
      progress: buildProgressFromEntries(entries),
      actionLabel: 'Open dashboard'
    });

    this.toastService.callErrorToast('Error uploading files, please try again later!', 'Upload Error', 5000);
    return throwError(() => error);
  }

  private processUploadResponseAndCreateProject(
    response: FilesUploadResponse,
    keywordIds: string[],
    projectName: string,
    projectId: string,
    taskRef: TaskRef,
    events: UploadManagerEvents
  ) {
    const entries = buildUploadEntries(response.allFiles);
    const progress = buildProgressFromEntries(entries);
    const message = buildUploadMessage(entries, 'documents');
    const taskStatus = buildTaskStatusFromEntries(entries);

    taskRef.update({
      status: taskStatus === 'success' ? 'running' : taskStatus,
      entries,
      progress,
      message
    });

    if (!response.ok) {
      taskRef.fail('An error happened while uploading your files to our system, please try again later.', {
        entries,
        progress,
        actionLabel: 'Open dashboard',
        actionDisabled: false,
        actionTooltip: undefined,
        route: '/dashboard'
      });
      this.toastService.callErrorToast(
        'An error happened while uploading your files to our system, please try again later.',
        undefined,
        5000
      );
      return EMPTY;
    }

    this.toastService.callSuccessToast(
      'Upload finalized with success, we will start creating your scan project.',
      undefined,
      5000
    );

    taskRef.update({
      status: 'running',
      message: 'Upload finished. Creating document scan project...',
      cancellable: false
    });

    const r2FilePaths = response.allFiles
      .filter((file) =>
        file.status === FileUploadStatus.UPLOADED || file.status === FileUploadStatus.UNCHANGED
      )
      .map((file) => `${file.userId}/${file.provider}/${file.fileId}-${file.modifiedTime}`);

    const parsedBody = this.parseCreateDocumentScan([], r2FilePaths, keywordIds, projectName, projectId);

    return this.createFilesScanProject(parsedBody).pipe(
      tap(() => {
        events.onProjectCreated?.();
        const summary = buildResponseSummary(response);
        if (summary.errorCount > 0) {
          taskRef.fail('Document upload finished with some errors.', {
            entries,
            progress,
            route: `/dashboard/document-scan/documents/${projectId}`,
            actionLabel: 'Open project',
            actionDisabled: false,
            actionTooltip: undefined
          });
          return;
        }

        taskRef.complete({
          entries,
          progress,
          message: 'Document scan project created successfully.',
          route: `/dashboard/document-scan/documents/${projectId}`,
          actionLabel: 'Open project',
          actionDisabled: false,
          actionTooltip: undefined
        });
      }),
      catchError((error) => {
        console.error('Error creating project', error);
        taskRef.fail('Error creating files scan project, please try again.', {
          entries,
          progress,
          route: '/dashboard',
          actionLabel: 'Open dashboard',
          actionDisabled: false,
          actionTooltip: undefined
        });
        this.toastService.callErrorToast('Error creating files scan project, please try again.', 'Scan Project Error', 5000);
        return throwError(() => error);
      })
    );
  }

  private createFilesScanProject(data: createFilesScanProject) {
    this.posthogService.track('document_scan_started', {
      projectName: data.name,
      id: data.projectId,
      fileCount: data.firebaseFilePaths.length + data.r2FilePaths.length
    });

    return this.scanProjectService.startDocumentScan(data);
  }

  private startPolling(projectId: string, taskRef: TaskRef, events: UploadManagerEvents): void {
    const polling$ = timer(0, 2000).pipe(
      switchMap(() => this.uploadService.getFileUploadStatus(projectId)),
      takeWhile(
        (response) =>
          response.allFiles.some((file) => file.status === FileUploadStatus.PENDING) ||
          response.allFiles.length === 0,
        true
      ),
      catchError((error) => {
        console.error(`Polling failed for project ${projectId}`, error);
        this.toastService.callErrorToast('Error verifying upload status.', 'Upload Error');
        taskRef.fail('Error verifying upload status.', {
          cancellable: false
        });
        return of({
          ok: false,
          allFiles: [],
          uploadedCount: 0,
          errorCount: 0,
          unchangedCount: 0
        } as FilesUploadResponse);
      }),
      share()
    );

    let responseCount = 0;
    const pollingSubscription = polling$
      .pipe(
        tap((response) => {
          if (response.allFiles.length > 0 && !responseCount) {
            responseCount += 1;
            const redirectSubscription = timer(2000).subscribe(() => {
              events.onProcessStarted?.();
            });
            this.taskRuntime.attachSubscription(taskRef.id, redirectSubscription);
          }

          const entries = buildUploadEntries(response.allFiles);
          taskRef.update({
            status: buildTaskStatusFromEntries(entries),
            entries,
            progress: buildProgressFromEntries(entries),
            message: buildUploadMessage(entries, 'documents')
          });
        })
      )
      .subscribe();

    this.taskRuntime.attachSubscription(taskRef.id, pollingSubscription);
  }

  private parseCreateDocumentScan(
    filepaths: string[],
    r2FilePaths: string[],
    keywords: string[],
    name: string,
    projectId?: string
  ): createFilesScanProject {
    return {
      firebaseFilePaths: filepaths,
      keywordIds: keywords,
      r2FilePaths,
      name,
      projectId
    };
  }

  private buildInitialEntries(files: File[] | FilesUploadBody) {
    if (!Array.isArray(files)) {
      return [];
    }

    const user = this.authService.getCurrentUser();

    return buildUploadEntries(
      files.map((file) => ({
        fileId: ulid(),
        name: file.name,
        userId: user?.uid ?? '',
        provider: DriversIntegrationTypes.LOCAL_PICKER,
        mimeType: file.type,
        modifiedTime: file.lastModified,
        progress: 0,
        size: file.size,
        error: false,
        status: FileUploadStatus.PENDING,
        uploadType: 'document',
        file
      }) as FileData)
    );
  }

  private cancelTask(taskRef: TaskRef): void {
    const currentTask = this.notificationCenter.items().find((item) => item.id === taskRef.id);
    const entries = currentTask?.type === 'task'
      ? (currentTask.entries ?? []).map((entry) =>
          entry.status === 'queued' || entry.status === 'running'
            ? { ...entry, status: 'canceled' as const }
            : entry
        )
      : [];

    taskRef.update({
      status: 'canceled',
      cancellable: false,
      entries,
      progress: buildProgressFromEntries(entries),
      message: 'Document upload canceled.',
      unread: true
    });
    this.toastService.callInformationToast('Files upload cancelled successfully!');
  }
}
